/**
 * User category definitions for Zeytune.
 *
 * User categories are a layer on top of pricing tiers that control what
 * effective tier a user is granted. This allows certain users (e.g. the
 * Zeytune development team, or manually elevated "power users") to receive
 * elevated access without going through the standard payment flow.
 *
 * ---
 * How categories map to effective tiers:
 *   - standard_user  → Uses the user's stored `plan` field as-is (free/pro/teams).
 *   - power_user     → Always receives Pro access, regardless of stored plan.
 *   - zeytune_team   → Always receives Teams access and bypasses all tier gates;
 *                      intended exclusively for the Zeytune development team.
 *
 * ---
 * To add a new category:
 *   1. Add its id to the `UserCategory` union type.
 *   2. Add its `UserCategoryDefinition` entry to `USER_CATEGORIES`.
 *   3. Add a case in `resolveEffectiveTier` if the new category needs custom
 *      tier resolution logic.
 */

import type { PricingTier } from './pricingTiers';
import type { UserModel } from '../types';

/** Union of all known user category identifiers. */
export type UserCategory = 'standard_user' | 'power_user' | 'zeytune_team';

/** Metadata describing a single user category. */
export interface UserCategoryDefinition {
    /** Unique identifier — must match the `UserCategory` key. */
    id: UserCategory;

    /** Human-readable display name. */
    name: string;

    /** Short description of who belongs to this category and what it grants them. */
    description: string;
}

/** Single source of truth for all available user categories. */
export const USER_CATEGORIES: Record<UserCategory, UserCategoryDefinition> = {
    /** Normal paying (or free) customers — tier is determined solely by their subscription. */
    standard_user: {
        id: 'standard_user',
        name: 'Standard User',
        description: 'A regular app user whose access is determined by their active subscription plan.',
    },

    /**
     * Elevated regular users who always receive Pro access without needing to pay.
     * Set this category in MongoDB to grant complimentary Pro access.
     */
    power_user: {
        id: 'power_user',
        name: 'Power User',
        description: 'A trusted user who receives permanent Pro access, independent of their subscription status.',
    },

    /**
     * Members of the Zeytune development team.
     * Always receive full Teams-level access and bypass all feature gates.
     */
    zeytune_team: {
        id: 'zeytune_team',
        name: 'Zeytune Team',
        description: 'A Zeytune team member who has unrestricted access to all features.',
    },
};

/**
 * Resolves the effective pricing tier for a user based on their category.
 *
 * The resolved tier is used by `useFeatureAccess` to determine which features
 * the user can access, independently of what their `plan` field stores.
 *
 * @param user - The user model from the store, or `null` when unauthenticated.
 * @returns The effective `PricingTier` to apply when checking feature access.
 */
export function resolveEffectiveTier(user: Pick<UserModel, 'plan' | 'userCategory'> | null): PricingTier {
    if (!user) return 'free';

    switch (user.userCategory) {
        case 'power_user':
            // Power users always receive Pro regardless of their stored plan.
            return 'pro';

        case 'zeytune_team':
            // Zeytune team members always receive the highest available tier.
            return 'teams';

        case 'standard_user':
        default:
            // Standard users (or users with no category set) use their stored plan.
            return user.plan ?? 'free';
    }
}

/**
 * Returns `true` if the user belongs to the Zeytune development team.
 *
 * Used in `useFeatureAccess` to bypass tier-based feature gates entirely,
 * so team members always have `'full'` access even for features not yet
 * included in any paid tier.
 *
 * @param user - The user model from the store, or `null` when unauthenticated.
 */
export function isZeytuneTeam(user: Pick<UserModel, 'userCategory'> | null): boolean {
    return user?.userCategory === 'zeytune_team';
}
